import type {
  CollapsedNodeGroup,
  GraphEdgesSettings,
  GraphResult,
  NodeId,
  NodeRecord,
  NodeSn,
} from '@ipfabric/utils';
import { prop, propOf } from '@ipfabric/utils/fp/prop';
import { collapseNodes } from 'components/Graph/commands/collapsing/helpers/collapseNodes';
import { getCollapsedGroupId } from 'components/Graph/commands/collapsing/helpers/collapsedEntityId';
import { buildNodesEdgesMap } from 'components/Graph/helpers/buildNodesEdgesMap';
import {
  filterExistingNodes,
  makeFilterExistingNodes,
} from 'components/Graph/helpers/filterExistingNodes';
import type {
  GraphData,
  HydratedCollapsedNodeGroups,
} from 'components/Graph/model/GraphData';
import { array, record } from 'fp-ts';
import { pipe } from 'fp-ts/lib/pipeable';
import { addCollapsedHiddenNodes } from './addCollapsedHiddenNodes';
import { addExpandedHiddenHosts } from './addExpandedHiddenHosts';
import { addExpandedHiddenNodes } from './addExpandedHiddenNodes';

const hiddenNodeSnsToIds = (
  hiddenNodeSns: NodeSn[],
  collapsedNodeGroups: CollapsedNodeGroup[],
  nodes: NodeRecord,
  graphResult: GraphResult,
): Set<NodeId> => {
  // we need a list of node sns inside collapsed groups because we need to filter collapsed groups for no longer
  // exisitng nodes. The easiest way is to get full list of SNs form raw server response.
  const existingNodeSnsAlsoInGroups = new Set(
    Object.values(graphResult.graphData.nodes).map(prop('sn')),
  );
  // recalculate collapse group ids (=== sns, they are same for collapse groups)
  // and find out which ones are hidden
  const hiddenCollapsedNodeIds = pipe(
    collapsedNodeGroups,
    array.chain((group) => {
      // to check whether the group was hidden, we must calculate its original id when the view was saved
      if (hiddenNodeSns.includes(getCollapsedGroupId(group.nodeSns))) {
        return [
          // but for current view, its id will be based only on existing nodes:
          getCollapsedGroupId(
            group.nodeSns.filter((sn) => existingNodeSnsAlsoInGroups.has(sn)),
          ),
        ];
      }
      return [];
    }),
  );

  const hiddenRegularNodeIds = pipe(
    nodes,
    record.filter((nodeData) => !!hiddenNodeSns?.includes(nodeData.sn)),
    (x) => record.keys<NodeId>(x),
  );

  return new Set([...hiddenRegularNodeIds, ...hiddenCollapsedNodeIds]);
};

const applyCollapsedNodeGroups =
  (
    edgesSettings: GraphEdgesSettings,
    oldGraph?: Partial<GraphData>,
    initialCollapsedNodeGroups?: CollapsedNodeGroup[],
  ) =>
  (graph: GraphData): GraphData => {
    const currentHydratedCollapsedNodeGroups:
      | HydratedCollapsedNodeGroups
      | undefined = oldGraph?.collapsedNodeGroups;

    // When e.g. loading a view, we know exactly what SNs should be collapsed in which groups,
    // so we use that information if we have it available.
    if (initialCollapsedNodeGroups) {
      const nodeIdsBySn: Record<NodeSn, NodeId> =
        initialCollapsedNodeGroups.length
          ? Object.values(graph.nodes).reduce(
              (nodes: Record<NodeSn, NodeId>, node) => {
                nodes[node.sn] = node.id;
                return nodes;
              },
              {},
            )
          : {};

      return initialCollapsedNodeGroups.reduce(
        (nextGraph, group) =>
          collapseNodes(
            nextGraph,
            edgesSettings,
            filterExistingNodes(
              new Set(group.nodeSns.map(propOf(nodeIdsBySn))),
              graph.nodes,
            ),
            group.position,
          ),
        graph,
      );
    }

    // If the current collapsed groups should be preserved (i.e. when we pass the `oldGraph` argument
    // to the `hydrateGraph` function), we recover the node IDs from them and perform the collapsing anew.
    if (currentHydratedCollapsedNodeGroups) {
      return Object.values(currentHydratedCollapsedNodeGroups).reduce(
        (nextGraph, group) => {
          const nodeIds = filterExistingNodes(
            new Set(record.keys(group.nodes)),
            nextGraph.nodes,
          );

          return collapseNodes(nextGraph, edgesSettings, nodeIds);
        },
        graph,
      );
    }
    return graph;
  };

const applyHiddenNodes =
  (
    graphResult: GraphResult,
    oldGraph?: Partial<GraphData>,
    collapsedNodeGroups?: CollapsedNodeGroup[],
    hiddenNodeSns?: NodeSn[],
  ) =>
  (graphData: GraphData): GraphData => {
    if (hiddenNodeSns) {
      return {
        ...graphData,
        hiddenNodes: hiddenNodeSnsToIds(
          hiddenNodeSns,
          collapsedNodeGroups ?? [],
          graphData.nodes,
          graphResult,
        ),
      };
    }
    if (oldGraph?.hiddenNodes) {
      const hiddenNodes = pipe(
        oldGraph.hiddenNodes,
        addExpandedHiddenNodes(graphData.nodes, oldGraph.nodes),
        addCollapsedHiddenNodes(graphData.nodes, oldGraph.nodes),
        addExpandedHiddenHosts(graphData.nodes, oldGraph.nodes),
        makeFilterExistingNodes(graphData.nodes),
      );

      return {
        ...graphData,
        hiddenNodes,
      };
    }
    return graphData;
  };

const applySelectedNodes =
  (oldGraph?: Partial<GraphData>) =>
  (graphData: GraphData): GraphData => {
    if (oldGraph?.selectedNodeIds) {
      return {
        ...graphData,
        selectedNodeIds: filterExistingNodes(
          oldGraph.selectedNodeIds,
          graphData.nodes,
        ),
      };
    }
    return graphData;
  };

/**
 * Transform graph result returned from API into frontend graph state
 *
 * @param graphResult the graph returned from server
 * @param oldGraph previous graph state to preserve some features from it (selected and hidden nodes)
 * @param collapsedNodeGroups list of collapsed node data, as collapsing is frontend-only feature reapplied on load
 * @param hiddenNodeSns if present, transforms to node ids and overrides hidden node ids from old graph
 */
export function hydrateGraph(
  graphResult: GraphResult,
  oldGraph?: Partial<GraphData>,
  collapsedNodeGroups?: CollapsedNodeGroup[],
  hiddenNodeSns?: NodeSn[],
): GraphData {
  const {
    boxLabels,
    graphData: { edges, nodes },
  } = graphResult;

  const rawGraphData: GraphData = {
    boxLabels,
    collapsedNodeGroups: {},
    edges,
    hiddenNodes: new Set(),
    nodes,
    /*
     * Nodes to edges map is built upon load rather than memoizing it for performance reasons.
     * We currently do not change node adjacency on frontend and memoization would have
     * either to be super smart (memoize only over edge source and target) or unnecessarily
     * recalculate the map on every other change in nodes or edges.
     */
    nodesEdgesMap: buildNodesEdgesMap(nodes, edges),
    selectedNodeIds: new Set(),
  };

  return pipe(
    rawGraphData,
    applyCollapsedNodeGroups(
      graphResult.settings.edges,
      oldGraph,
      collapsedNodeGroups,
    ),
    applyHiddenNodes(graphResult, oldGraph, collapsedNodeGroups, hiddenNodeSns),
    applySelectedNodes(oldGraph),
  );
}
